import React, { useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

const AdminDataTable = ({ columns, rows = [], emptyText = "No records found" }) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      columns.some((col) => {
        const val = row[col.key];
        return String(val ?? "").toLowerCase().includes(q);
      })
    );
  }, [rows, columns, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const slice = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="admin-ui admin-table-wrap">
      <div className="card basic-data-table mb-0">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <span className="text-secondary-light" style={{ fontSize: "0.88rem" }}>
              {filtered.length} record{filtered.length === 1 ? "" : "s"}
            </span>
            <div className="position-relative" style={{ maxWidth: 280, width: "100%" }}>
              <Icon
                icon="mdi:magnify"
                width={18}
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#667781",
                }}
              />
              <input
                type="text"
                className="form-control radius-8"
                style={{ paddingLeft: 36 }}
                placeholder="Search…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
          <div className="table-responsive">
            <table className="table bordered-table mb-0">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slice.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="admin-empty">
                      {emptyText}
                    </td>
                  </tr>
                ) : (
                  slice.map((row, idx) => (
                    <tr key={row.id ?? idx}>
                      {columns.map((col) => (
                        <td key={col.key}>
                          {col.render ? col.render(row[col.key], row) : row[col.key]}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="pagination-controls d-flex flex-wrap align-items-center gap-2 justify-content-between mt-24">
            <span className="pagination-meta">
              Showing{" "}
              {filtered.length === 0
                ? "0 to 0"
                : `${(currentPage - 1) * pageSize + 1} to ${Math.min(
                    currentPage * pageSize,
                    filtered.length
                  )}`}{" "}
              of {filtered.length} entries
            </span>
            <ul className="pagination d-flex flex-wrap align-items-center gap-2 mb-0">
              <li className="page-item">
                <button
                  type="button"
                  className="page-link"
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  aria-label="Previous page"
                >
                  <Icon icon="ep:d-arrow-left" className="text-xl" />
                </button>
              </li>
              <li className="page-item">
                <button type="button" className="page-link is-active" aria-current="page">
                  {currentPage}
                </button>
              </li>
              <li className="page-item">
                <button
                  type="button"
                  className="page-link"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  aria-label="Next page"
                >
                  <Icon icon="ep:d-arrow-right" className="text-xl" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDataTable;
